import { Container } from '@/components/ui';
import { SUBSCRIPTION_TIERS } from '@/sections/settings/subscription/constant';
import { cn } from '@/utils/classname';

// Derive display names from SUBSCRIPTION_TIERS (single source of truth)
const tierDisplayNames = Object.fromEntries(
  SUBSCRIPTION_TIERS.map(tier => [tier.id, tier.displayName])
) as Record<string, string>;

export interface SubscriptionTierSelectorProps {
  selectedTier: string;
  onTierChange: (tier: string) => void;
  disabled?: boolean;
  tiers?: string[];
  className?: string;
}

/**
 * SubscriptionTierSelector - Reusable component for subscription tier selection
 * Extracted from configure/index.tsx for use in both settings and wizard
 */
export function SubscriptionTierSelector({
  selectedTier,
  onTierChange,
  disabled = false,
  tiers = ['freemium', 'foundational', 'professional', 'ultimate'],
  className,
}: SubscriptionTierSelectorProps) {
  return (
    <section
      className={cn('space-y-2', className)}
      aria-labelledby="tier-section-title"
    >
      <h3
        id="tier-section-title"
        className="text-chariot-text-primary text-sm font-semibold"
      >
        Subscription Tier
      </h3>
      <div aria-describedby="tier-description">
        <p id="tier-description" className="sr-only">
          Select your subscription tier to access different features and
          capabilities
        </p>
        <div
          role="radiogroup"
          aria-label="Subscription tier"
          className={cn(
            'grid gap-3',
            tiers.length === 4
              ? 'grid-cols-4'
              : tiers.length === 3
                ? 'grid-cols-3'
                : tiers.length === 2
                  ? 'grid-cols-2'
                  : 'grid-cols-1'
          )}
        >
          {tiers.map(tier => {
            const isSelected = selectedTier === tier;
            const displayName =
              tierDisplayNames[tier as keyof typeof tierDisplayNames] || tier;

            return (
              <Container
                key={tier}
                onClick={() => !disabled && onTierChange(tier)}
                className={cn(
                  'relative cursor-pointer py-2 text-center',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
                isSelected={isSelected}
                role="radio"
                aria-checked={isSelected}
                data-testid={`tier-button-${tier}`}
              >
                {isSelected && (
                  <div className="bg-chariot-brand-primary absolute -top-2.5 left-1/2 -translate-x-1/2 transform rounded-full px-2 py-0.5 text-[10px] font-bold text-white">
                    YOUR PLAN
                  </div>
                )}
                <span className="text-sm font-medium">{displayName}</span>
              </Container>
            );
          })}
        </div>
      </div>
    </section>
  );
}
